import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { SafeAreaProvider, type Edge } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import type {
  LeagueWeekBetScoringOverride,
  PickLockPolicyConfig,
  SeasonTemplateDetail,
  SeasonTemplateInstanceConfig,
  SeasonTemplateSummary,
  SeasonTemplateWeekConfig,
  SeasonTemplateBetType,
} from '@shared/seasons';
import { applyTotalTiebreakerToggle } from '@shared/seasons';
import { AppSelect } from '@/components/ui/AppSelect';
import { Input } from './ui/Input';
import { Switch } from './ui/Switch';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { useToast } from '@/hooks/useToast';
import AppSafeArea from '@/components/layout/AppSafeArea';
import { clientLogger } from '@/lib/datadog';
import { useTRPC } from '@/lib/trpc';

const flexStyle = { flex: 1 };
const modalScrollContentStyle = { paddingBottom: 120 };
const detailLoadingStyle = { marginTop: 12 };

type BetOverrideMode = 'default' | 'multiplier' | 'fixed';
type SeasonScoringSystem = 'oddsBased' | 'totalWins';
type SeasonWeekMode = 'standard' | 'propWeeklyOnly';
type PickLockMode = PickLockPolicyConfig['mode'];

const SCORING_OVERRIDE_SIDES = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
] as const;

const SCORING_OVERRIDE_SIDE_SET = new Set<string>(SCORING_OVERRIDE_SIDES);

const formatDateLabel = (value: string | Date | null | undefined) => {
  if (!value) return '';
  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }
  return value;
};

const getTemplateMetadataValue = (
  metadata: Record<string, unknown> | undefined | null,
  key: string,
): string | boolean | undefined => {
  const value = metadata?.[key];
  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }
  return undefined;
};

interface BetFormState {
  nflBetId: string;
  enabled: boolean;
  mode: BetOverrideMode;
  multiplier: string;
  sideValues: Record<string, string>;
  overrideSides: { side: string; label: string }[];
  sortOrder: number | null;
}

interface WeekFormState {
  weekId: string;
  enabled: boolean;
  propOfTheWeekNflBetId?: string | null;
  bets: BetFormState[];
}

export interface SeasonBuilderInitialState {
  templateId: string;
  label: string;
  weeks: SeasonTemplateWeekConfig[];
  scoringSystem: SeasonScoringSystem;
  seasonWeekMode?: SeasonWeekMode;
  pickLockPolicy?: PickLockPolicyConfig;
  weeklyBuyIn?: string | undefined;
  seasonBuyIn?: string | undefined;
  isActive?: boolean | undefined;
  additionalInfo?: Record<string, unknown> | null;
}

export interface SeasonBuilderResult {
  templateSummary: SeasonTemplateSummary;
  label: string;
  config: SeasonTemplateInstanceConfig;
  scoringSystem: SeasonScoringSystem;
  seasonWeekMode: SeasonWeekMode;
  pickLockPolicy: PickLockPolicyConfig;
  weeklyBuyIn?: string | undefined;
  seasonBuyIn?: string | undefined;
  isActive?: boolean | undefined;
  additionalInfo?: Record<string, unknown> | null;
}

interface SeasonTemplateBuilderProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (result: SeasonBuilderResult) => Promise<void> | void;
  initialState?: SeasonBuilderInitialState | null;
  submitLabel?: string;
  mode?: 'create' | 'edit';
  lockedBetIds?: string[] | Set<string>;
  templateLocked?: boolean;
  disableWeekToggle?: boolean;
  presentation?: 'modal' | 'screen';
  safeAreaEdges?: Edge[];
  onDirtyChange?: (hasChanges: boolean) => void;
  leagueId?: string;
  seasonId?: string;
}

type PropOfTheWeekSearchResult = {
  id: string;
  nflGameId: string;
  displayInfo?: { label?: string; description?: string; sideLabelOne?: string; sideLabelTwo?: string } | null;
  game?: { homeTeam?: { abbreviation?: string } | null; awayTeam?: { abbreviation?: string } | null } | null;
};

function PropOfTheWeekSelector({
  leagueId,
  seasonId,
  weekId,
  value,
  onChange,
  disabled,
}: {
  leagueId?: string;
  seasonId?: string;
  weekId: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const query = search.trim();
  const trpc = useTRPC();
  const { data: props = [], isLoading } = useQuery<PropOfTheWeekSearchResult[]>(
    trpc.seasons.propBets.queryOptions(
      {
        leagueId: leagueId ?? '',
        seasonId: seasonId ?? '',
        weekId,
        search: query || undefined,
      },
      { enabled: Boolean(leagueId && seasonId && weekId) },
    ) as any,
  );
  const selected = props.find((prop) => prop.id === value);

  if (!leagueId || !seasonId) return null;

  return (
    <View style={styles.propOfTheWeekBox}>
      <Text style={styles.smallLabel}>Prop Bet of the Week</Text>
      <Text style={styles.propOfTheWeekHint}>
        Search the props available for this week and choose one featured pick.
      </Text>
      {selected && (
        <View style={styles.selectedPropRow}>
          <Text style={styles.selectedPropLabel} numberOfLines={2}>
            {selected.displayInfo?.label ?? 'Selected prop'}
          </Text>
          <TouchableOpacity onPress={() => onChange(null)} disabled={disabled}>
            <Text style={styles.clearPropText}>Clear</Text>
          </TouchableOpacity>
        </View>
      )}
      <Input
        value={search}
        onChangeText={(text) => {
          setSearch(text);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Search props by name or option"
        editable={!disabled}
      />
      {isOpen && !disabled && (
        <View style={styles.propSearchResults}>
          {isLoading ? (
            <ActivityIndicator />
          ) : props.length === 0 ? (
            <Text style={styles.smallLabel}>No matching props found.</Text>
          ) : (
            props.slice(0, 8).map((prop) => (
              <TouchableOpacity
                key={prop.id}
                style={styles.propSearchResult}
                onPress={() => {
                  onChange(prop.id);
                  setSearch('');
                  setIsOpen(false);
                }}
              >
                <Text style={styles.propSearchResultLabel}>{prop.displayInfo?.label ?? 'Custom prop'}</Text>
                <Text style={styles.smallLabel}>
                  {[prop.game?.homeTeam?.abbreviation, prop.game?.awayTeam?.abbreviation].filter(Boolean).join(' vs ')}
                </Text>
              </TouchableOpacity>
            ))
          )}
        </View>
      )}
      {!value && !isOpen && <Text style={styles.smallLabel}>No prop selected for this week.</Text>}
    </View>
  );
}

const DEFAULT_MULTIPLIER = '1';
const DEFAULT_POINTS = '1';
const DEFAULT_PICK_LOCK_POLICY: PickLockPolicyConfig = { mode: 'gameStart' };

const buildOverrideSideDefinitions = (bet: SeasonTemplateDetail['weeks'][number]['games'][number]['bets'][number]) => {
  const displayInfo = bet.displayInfo as Record<string, unknown> | null;

  if (bet.nflBetType === 'custom_prop') {
    const options = Array.isArray(displayInfo?.options) ? displayInfo?.options : null;
    if (options) {
      const sides = options
        .map((option, index) => {
          const side = typeof option?.side === 'string' ? option.side.trim() : '';
          const label =
            typeof option?.label === 'string' && option.label.trim()
              ? option.label.trim()
              : `Side ${index + 1}`;
          return { side, label };
        })
        .filter((entry) => SCORING_OVERRIDE_SIDE_SET.has(entry.side));

      const deduped = new Map<string, { side: string; label: string }>();
      sides.forEach((entry) => {
        if (!deduped.has(entry.side)) deduped.set(entry.side, entry);
      });
      const result = Array.from(deduped.values());
      if (result.length >= 2) return result;
    }

    const sideLabelOne =
      typeof displayInfo?.sideLabelOne === 'string' && displayInfo.sideLabelOne.trim()
        ? displayInfo.sideLabelOne.trim()
        : 'Side 1';
    const sideLabelTwo =
      typeof displayInfo?.sideLabelTwo === 'string' && displayInfo.sideLabelTwo.trim()
        ? displayInfo.sideLabelTwo.trim()
        : 'Side 2';

    return [
      { side: 'one', label: sideLabelOne },
      { side: 'two', label: sideLabelTwo },
    ];
  }

  if (bet.nflBetType === 'total') {
    return [
      { side: 'one', label: 'Over' },
      { side: 'two', label: 'Under' },
    ];
  }

  if (bet.nflBetType === 'total_tiebreaker') {
    return [
      { side: 'one', label: 'Tiebreaker' },
      { side: 'two', label: 'Tiebreaker' },
    ];
  }

  if (bet.nflBetType === 'moneyline' || bet.nflBetType === 'spread') {
    return [
      { side: 'one', label: 'Home' },
      { side: 'two', label: 'Away' },
    ];
  }

  return [
    { side: 'one', label: 'Side 1' },
    { side: 'two', label: 'Side 2' },
  ];
};

export default function SeasonTemplateBuilder({
  visible,
  onClose,
  onSubmit,
  initialState,
  submitLabel = 'Save Season',
  mode = 'create',
  lockedBetIds,
  templateLocked = false,
  disableWeekToggle = false,
  presentation = 'modal',
  safeAreaEdges,
  onDirtyChange,
  leagueId,
  seasonId,
}: SeasonTemplateBuilderProps) {
  const trpc = useTRPC();
  const { toast } = useToast();
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialState?.templateId ?? '');
  const [seasonLabel, setSeasonLabel] = useState(initialState?.label ?? '');
  const [seasonScoring, setSeasonScoring] = useState<SeasonScoringSystem>(initialState?.scoringSystem ?? 'oddsBased');
  const [seasonWeekMode, setSeasonWeekMode] = useState<SeasonWeekMode>(initialState?.seasonWeekMode ?? 'standard');
  const [pickLockMode, setPickLockMode] = useState<PickLockMode>(
    initialState?.pickLockPolicy?.mode ?? DEFAULT_PICK_LOCK_POLICY.mode,
  );
  const [pickLockGamesCount, setPickLockGamesCount] = useState(
    String(initialState?.pickLockPolicy?.gamesCount ?? 1),
  );
  const [weeklyBuyIn, setWeeklyBuyIn] = useState(initialState?.weeklyBuyIn ?? '');
  const [seasonBuyIn, setSeasonBuyIn] = useState(initialState?.seasonBuyIn ?? '');
  const [isActive, setIsActive] = useState(initialState?.isActive ?? true);
  const [superBowlSquaresEnabled, setSuperBowlSquaresEnabled] = useState(
    Boolean(initialState?.additionalInfo?.superBowlSquaresEnabled),
  );
  const [weekStates, setWeekStates] = useState<WeekFormState[]>([]);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const initialSnapshotRef = useRef<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const resolvedSafeAreaEdges =
    safeAreaEdges ?? (presentation === 'screen' ? ['bottom'] : ['top', 'bottom']);

  const { data: templateSummaries, isLoading: summariesLoading } = useQuery(
    trpc.seasons.templates.list.queryOptions(undefined, { enabled: visible }),
  );

  const { data: templateDetail, isLoading: detailLoading } = useQuery(
    trpc.seasons.templates.detail.queryOptions(
      { templateId: selectedTemplateId ?? '' },
      { enabled: visible && !!selectedTemplateId },
    ),
  );

  const currentSnapshot = useMemo(() => {
    const weeks = weekStates.map((week) => ({
      weekId: week.weekId,
      enabled: week.enabled,
      propOfTheWeekNflBetId: week.propOfTheWeekNflBetId ?? null,
      bets: [...week.bets]
        .sort((a, b) => a.nflBetId.localeCompare(b.nflBetId))
        .map((bet) => ({
          nflBetId: bet.nflBetId,
          enabled: bet.enabled,
          mode: bet.mode,
          multiplier: bet.multiplier,
          sideValues: bet.sideValues,
          overrideSides: bet.overrideSides,
          sortOrder: bet.sortOrder,
        })),
    }));
    return JSON.stringify({
      templateId: selectedTemplateId,
      seasonLabel,
      seasonScoring,
      seasonWeekMode,
      pickLockMode,
      pickLockGamesCount,
      weeklyBuyIn,
      seasonBuyIn,
      isActive,
      superBowlSquaresEnabled,
      weeks,
    });
  }, [
    selectedTemplateId,
    seasonLabel,
    seasonScoring,
    seasonWeekMode,
    pickLockMode,
    pickLockGamesCount,
    weeklyBuyIn,
    seasonBuyIn,
    isActive,
    superBowlSquaresEnabled,
    weekStates,
  ]);

  useEffect(() => {
    if (!visible) {
      initialSnapshotRef.current = null;
      if (hasChanges) {
        setHasChanges(false);
        if (onDirtyChange) {
          onDirtyChange(false);
        }
      }
      return;
    }

    if (!templateDetail) return;

    const isReady =
      Boolean(selectedTemplateId) &&
      (weekStates.length > 0 || templateDetail.weeks.length === 0);

    if (!isReady) return;

    if (!initialSnapshotRef.current) {
      initialSnapshotRef.current = currentSnapshot;
      if (hasChanges) {
        setHasChanges(false);
      }
      if (onDirtyChange) {
        onDirtyChange(false);
      }
      return;
    }

    const nextHasChanges = currentSnapshot !== initialSnapshotRef.current;
    if (nextHasChanges !== hasChanges) {
      setHasChanges(nextHasChanges);
      if (onDirtyChange) {
        onDirtyChange(nextHasChanges);
      }
    }
  }, [
    currentSnapshot,
    hasChanges,
    onDirtyChange,
    selectedTemplateId,
    templateDetail,
    visible,
    weekStates.length,
  ]);

  const defaultTemplateId = useMemo(() => {
    if (selectedTemplateId) return selectedTemplateId;
    return templateSummaries?.[0]?.id ?? '';
  }, [selectedTemplateId, templateSummaries]);

  useEffect(() => {
    if (!visible) {
      setSelectedTemplateId(initialState?.templateId ?? '');
      setSeasonLabel(initialState?.label ?? '');
      setSeasonScoring(initialState?.scoringSystem ?? 'oddsBased');
      setSeasonWeekMode(initialState?.seasonWeekMode ?? 'standard');
      setPickLockMode(initialState?.pickLockPolicy?.mode ?? DEFAULT_PICK_LOCK_POLICY.mode);
      setPickLockGamesCount(String(initialState?.pickLockPolicy?.gamesCount ?? 1));
      setWeeklyBuyIn(initialState?.weeklyBuyIn ?? '');
      setSeasonBuyIn(initialState?.seasonBuyIn ?? '');
      setIsActive(initialState?.isActive ?? true);
      setSuperBowlSquaresEnabled(Boolean(initialState?.additionalInfo?.superBowlSquaresEnabled));
      if (!initialState) {
        setWeekStates([]);
        setExpandedWeeks(new Set());
      }
      return;
    }

    if (initialState) {
      setSelectedTemplateId(initialState.templateId);
      setSeasonLabel(initialState.label);
      setSeasonScoring(initialState.scoringSystem);
      setSeasonWeekMode(initialState.seasonWeekMode ?? 'standard');
      setPickLockMode(initialState.pickLockPolicy?.mode ?? DEFAULT_PICK_LOCK_POLICY.mode);
      setPickLockGamesCount(String(initialState.pickLockPolicy?.gamesCount ?? 1));
      setWeeklyBuyIn(initialState.weeklyBuyIn ?? '');
      setSeasonBuyIn(initialState.seasonBuyIn ?? '');
      setIsActive(initialState.isActive ?? true);
      setSuperBowlSquaresEnabled(Boolean(initialState.additionalInfo?.superBowlSquaresEnabled));
    }
  }, [visible, initialState]);

  useEffect(() => {
    if (!visible) return;
    if (!selectedTemplateId && defaultTemplateId) {
      setSelectedTemplateId(defaultTemplateId);
    }
  }, [visible, defaultTemplateId, selectedTemplateId]);

  useEffect(() => {
    if (!templateDetail) return;
    let initialWeeks: SeasonTemplateWeekConfig[] = [];
    if (initialState && initialState.templateId === templateDetail.id) {
      initialWeeks = initialState.weeks;
    }
    if (!seasonLabel && !initialState?.label) {
      setSeasonLabel(templateDetail.label);
    }
    const nextWeekStates: WeekFormState[] = templateDetail.weeks.map((week) => {
      const initialWeek = initialWeeks.find((w) => w.weekId === week.week.id);
      const betMap = new Map(initialWeek?.bets?.map((bet) => [bet.nflBetId, bet]));
      const bets: BetFormState[] = [];
      week.games.forEach((game) => {
        let customPropIndex = 0;
        game.bets.forEach((bet) => {
          const existing = betMap.get(bet.id);
          const override = existing?.scoringOverride;
          const overrideSides = buildOverrideSideDefinitions(bet);
          const sideValues: Record<string, string> = {};
          overrideSides.forEach((entry) => {
            let value: number | undefined;
            if (override && override.type === 'nPoints') {
              value = (override as any)[entry.side];
              if (value === undefined && entry.side === 'one' && typeof override.side1 === 'number') {
                value = override.side1;
              }
              if (value === undefined && entry.side === 'two' && typeof override.side2 === 'number') {
                value = override.side2;
              }
            }
            sideValues[entry.side] = String(value ?? DEFAULT_POINTS);
          });
          const mode: BetOverrideMode = !override
            ? 'default'
            : override.type === 'nPoints'
              ? 'fixed'
              : 'multiplier';
          const initialSortOrder =
            typeof existing?.sortOrder === 'number' && Number.isFinite(existing.sortOrder)
              ? existing.sortOrder
              : bet.nflBetType === 'custom_prop'
                ? customPropIndex
                : null;
          bets.push({
            nflBetId: bet.id,
            enabled: existing ? existing.enabled !== false : true,
            mode,
            multiplier:
              override && override.type === 'oddsBased'
                ? String(override.multiplier ?? DEFAULT_MULTIPLIER)
                : DEFAULT_MULTIPLIER,
            sideValues,
            overrideSides,
            sortOrder: initialSortOrder,
          });
          if (bet.nflBetType === 'custom_prop') {
            customPropIndex += 1;
          }
        });
      });
      return {
        weekId: week.week.id,
        enabled: initialWeek ? initialWeek.enabled !== false : true,
        propOfTheWeekNflBetId: initialWeek?.propOfTheWeekNflBetId ?? null,
        bets,
      };
    });
    setWeekStates(nextWeekStates);
    if (nextWeekStates.length > 0) {
      setExpandedWeeks((prev) => {
        if (prev.size > 0) return prev;
        const firstWeek = nextWeekStates[0];
        return firstWeek ? new Set([firstWeek.weekId]) : prev;
      });
    }
  }, [templateDetail, initialState, seasonLabel]);

  const lockedBetIdSet = React.useMemo(() => {
    if (!lockedBetIds) return new Set<string>();
    if (lockedBetIds instanceof Set) return lockedBetIds;
    return new Set(lockedBetIds);
  }, [lockedBetIds]);

  const betTypeById = useMemo(() => {
    const map = new Map<string, SeasonTemplateBetType>();
    if (!templateDetail) return map;
    templateDetail.weeks.forEach((week) => {
      week.games.forEach((game) => {
        game.bets.forEach((bet) => {
          map.set(bet.id, bet.nflBetType);
        });
      });
    });
    return map;
  }, [templateDetail]);

  const betStateById = useMemo(() => {
    const map = new Map<string, BetFormState>();
    weekStates.forEach((week) => {
      week.bets.forEach((bet) => {
        map.set(bet.nflBetId, bet);
      });
    });
    return map;
  }, [weekStates]);

  const betTypeStats = useMemo(() => {
    const stats: Record<
      SeasonTemplateBetType,
      { totalCount: number; enabledCount: number; mutableCount: number; enabledMutableCount: number }
    > = {
      moneyline: { totalCount: 0, enabledCount: 0, mutableCount: 0, enabledMutableCount: 0 },
      spread: { totalCount: 0, enabledCount: 0, mutableCount: 0, enabledMutableCount: 0 },
      total: { totalCount: 0, enabledCount: 0, mutableCount: 0, enabledMutableCount: 0 },
      total_tiebreaker: { totalCount: 0, enabledCount: 0, mutableCount: 0, enabledMutableCount: 0 },
      custom_prop: { totalCount: 0, enabledCount: 0, mutableCount: 0, enabledMutableCount: 0 },
    };

    if (!templateDetail) return stats;

    templateDetail.weeks.forEach((week) => {
      week.games.forEach((game) => {
        game.bets.forEach((bet) => {
          const betState = betStateById.get(bet.id);
          const enabled = betState?.enabled ?? true;
          const isLocked = lockedBetIdSet.has(bet.id);
          const record = stats[bet.nflBetType];
          record.totalCount += 1;
          if (enabled) record.enabledCount += 1;
          if (!isLocked) {
            record.mutableCount += 1;
            if (enabled) record.enabledMutableCount += 1;
          }
        });
      });
    });

    return stats;
  }, [templateDetail, betStateById, lockedBetIdSet]);

  const updateBetTypeToggle = (betType: SeasonTemplateBetType, enabled: boolean) => {
    if (!templateDetail) return;
    if (betType === 'total_tiebreaker') {
      setWeekStates((prev) => applyTotalTiebreakerToggle(prev, templateDetail, lockedBetIdSet, enabled));
      return;
    }
    setWeekStates((prev) =>
      prev.map((week) => ({
        ...week,
        bets: week.bets.map((bet) => {
          if (betTypeById.get(bet.nflBetId) !== betType) return bet;
          if (lockedBetIdSet.has(bet.nflBetId)) return bet;
          return { ...bet, enabled };
        }),
      })),
    );
  };

  const isTemplateLocked = templateLocked || mode === 'edit';

  const selectedTemplateLabel = React.useMemo(() => {
    if (templateDetail?.label) return templateDetail.label;
    const summary = templateSummaries?.find((item) => item.id === selectedTemplateId);
    return summary?.label ?? selectedTemplateId;
  }, [templateDetail, templateSummaries, selectedTemplateId]);

  const selectedTemplateSummary = useMemo(
    () => templateSummaries?.find((item) => item.id === selectedTemplateId) ?? null,
    [templateSummaries, selectedTemplateId],
  );

  const selectedTemplateMetadata = templateDetail?.metadata ?? selectedTemplateSummary?.metadata ?? null;
  const unitLabelSingular =
    (getTemplateMetadataValue(selectedTemplateMetadata, 'unitLabelSingular') as string | undefined) ?? 'Week';
  const supportsSquares =
    (getTemplateMetadataValue(selectedTemplateMetadata, 'supportsSquares') as boolean | undefined) ?? true;

  const toggleWeekExpanded = (weekId: string) => {
    setExpandedWeeks((prev) => {
      const clone = new Set(prev);
      if (clone.has(weekId)) {
        clone.delete(weekId);
      } else {
        clone.add(weekId);
      }
      return clone;
    });
  };

  const updateWeek = (weekId: string, updater: (week: WeekFormState) => WeekFormState) => {
    setWeekStates((prev) => prev.map((week) => (week.weekId === weekId ? updater(week) : week)));
  };

  const updateBet = (weekId: string, betId: string, updater: (bet: BetFormState) => BetFormState) => {
    updateWeek(weekId, (week) => ({
      ...week,
      bets: week.bets.map((bet) => (bet.nflBetId === betId ? updater(bet) : bet)),
    }));
  };

  const buildScoringOverride = (bet: BetFormState): LeagueWeekBetScoringOverride | undefined => {
    if (bet.mode === 'multiplier') {
      const numericMultiplier = Number(bet.multiplier);
      if (bet.multiplier.trim() === '' || !Number.isFinite(numericMultiplier) || numericMultiplier < 0) {
        throw new Error('Each multiplier override must be greater than or equal to 0.');
      }
      return { type: 'oddsBased', multiplier: numericMultiplier };
    }
    if (bet.mode === 'fixed') {
      const result: Record<string, number> = {};
      bet.overrideSides.forEach((entry) => {
        const rawValue = bet.sideValues[entry.side] ?? '';
        const numeric = Number(rawValue);
        if (rawValue.trim() === '' || !Number.isFinite(numeric)) {
          throw new Error('Fixed point overrides must include valid numbers for each side.');
        }
        result[entry.side] = numeric;
      });
      if (result.one === undefined) {
        const fallback = Number(bet.sideValues.one ?? DEFAULT_POINTS);
        result.one = Number.isFinite(fallback) ? fallback : Number(DEFAULT_POINTS);
      }
      if (result.two === undefined) {
        const fallback = Number(bet.sideValues.two ?? DEFAULT_POINTS);
        result.two = Number.isFinite(fallback) ? fallback : Number(DEFAULT_POINTS);
      }
      return { type: 'nPoints', ...result } as LeagueWeekBetScoringOverride;
    }
    return undefined;
  };

  const buildConfig = (): SeasonTemplateInstanceConfig => {
    if (!selectedTemplateId) {
      throw new Error('Select a template before saving.');
    }
    const weeks: SeasonTemplateWeekConfig[] = weekStates.map((week) => ({
      weekId: week.weekId,
      enabled: week.enabled,
      propOfTheWeekNflBetId: week.propOfTheWeekNflBetId,
      bets: week.bets.map((bet) => {
        const override = bet.enabled ? buildScoringOverride(bet) : undefined;
        return {
          nflBetId: bet.nflBetId,
          enabled: bet.enabled,
          scoringOverride: override ?? null,
          sortOrder: typeof bet.sortOrder === 'number' && Number.isFinite(bet.sortOrder) ? bet.sortOrder : null,
        };
      }),
    }));
    return { templateId: selectedTemplateId, weeks };
  };

  const handleSubmit = async () => {
    if (!templateDetail) {
      toast.show({ type: 'error', title: 'Select Template', description: 'Choose a template to continue.' });
      return;
    }
    if (!seasonLabel.trim()) {
      toast.show({ type: 'error', title: 'Season Label Required', description: 'Enter a label for this season.' });
      return;
    }
    const templateSummary = templateSummaries?.find((item) => item.id === templateDetail.id);
    if (!templateSummary) {
      toast.show({
        type: 'error',
        title: 'Template Missing',
        description: 'Unable to load template details. Please try again.',
      });
      return;
    }
    try {
      setSubmitting(true);
      const config = buildConfig();
      const parsedGamesCount = Number.parseInt(pickLockGamesCount, 10);
      const pickLockPolicy: PickLockPolicyConfig =
        pickLockMode === 'afterFirstNGames'
          ? {
              mode: pickLockMode,
              gamesCount: Number.isFinite(parsedGamesCount) && parsedGamesCount > 0 ? parsedGamesCount : 1,
            }
          : { mode: pickLockMode };
      const additionalInfo = {
        ...(initialState?.additionalInfo ?? {}),
        superBowlSquaresEnabled,
      };
      await onSubmit({
        templateSummary,
        label: seasonLabel.trim(),
        config,
        scoringSystem: seasonScoring,
        seasonWeekMode,
        pickLockPolicy,
        weeklyBuyIn: weeklyBuyIn.trim(),
        seasonBuyIn: seasonBuyIn.trim(),
        isActive,
        additionalInfo,
      });
      setSubmitting(false);
      onClose();
    } catch (error: any) {
      clientLogger.error('Season template save error', { error });
      toast.show({
        type: 'error',
        title: 'Unable to Save',
        description: error?.message ?? 'Failed to save season settings.',
      });
      setSubmitting(false);
    }
  };

  const renderBetControls = (
    week: WeekFormState,
    bet: BetFormState,
    meta?: { title: string; subtitle?: string; details?: string },
    reorder?: { canMoveUp: boolean; canMoveDown: boolean; onMoveUp: () => void; onMoveDown: () => void },
  ) => {
    const isLocked = lockedBetIdSet.has(bet.nflBetId);
    const switchDisabled = !week.enabled || isLocked;
    const inputsDisabled = !week.enabled;

    return (
      <View key={bet.nflBetId} style={styles.betRow}>
        <View style={styles.betHeader}>
          <View style={flexStyle}>
            <Text style={styles.betTitle}>{meta?.title ?? bet.nflBetId}</Text>
            {Boolean(meta?.subtitle) && <Text style={styles.betSubtitle}>{meta?.subtitle}</Text>}
            {Boolean(meta?.details) && <Text style={styles.betMeta}>{meta?.details}</Text>}
          </View>
          <View style={styles.betToggle}>
            <Text style={styles.smallLabel}>Enabled</Text>
            <Switch
              checked={bet.enabled}
              onCheckedChange={(value) =>
                updateBet(week.weekId, bet.nflBetId, (prev) => ({
                  ...prev,
                  enabled: value,
                }))
              }
              disabled={switchDisabled}
            />
          </View>
          {isLocked && <Text style={styles.lockedBadge}>Locked</Text>}
        </View>
        {reorder && (
          <View style={styles.reorderControls}>
            <TouchableOpacity
              style={[styles.reorderButton, !reorder.canMoveUp && styles.reorderButtonDisabled]}
              onPress={reorder.onMoveUp}
              disabled={!reorder.canMoveUp || inputsDisabled}
            >
              <Text style={styles.reorderButtonText}>Move Up</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.reorderButton, !reorder.canMoveDown && styles.reorderButtonDisabled]}
              onPress={reorder.onMoveDown}
              disabled={!reorder.canMoveDown || inputsDisabled}
            >
              <Text style={styles.reorderButtonText}>Move Down</Text>
            </TouchableOpacity>
          </View>
        )}
        {bet.enabled && (
          <>
          {isLocked && (
            <Text style={[styles.smallLabel, styles.lockedNotice]}>
              Bet is locked. Scoring updates allowed, but cannot be removed.
            </Text>
          )}
          <View style={styles.overrideContainer}>
            <Text style={styles.smallLabel}>Scoring Override</Text>
            <View style={styles.modeSelector}>
              {(['default', 'multiplier', 'fixed'] as BetOverrideMode[]).map((mode) => (
                <TouchableOpacity
                  key={mode}
                  style={[styles.modeChip, bet.mode === mode && styles.modeChipActive]}
                  onPress={() => updateBet(week.weekId, bet.nflBetId, (prev) => ({ ...prev, mode }))}
                  disabled={inputsDisabled}
                >
                  <Text style={[styles.modeChipText, bet.mode === mode && styles.modeChipTextActive]}>
                    {mode === 'default' ? 'Season Default' : mode === 'multiplier' ? 'Multiplier' : 'Fixed Points'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {bet.mode === 'multiplier' && (
              <View style={styles.inlineInputs}>
                <Input
                  value={bet.multiplier}
                  onChangeText={(text) =>
                     updateBet(week.weekId, bet.nflBetId, (prev) => ({ ...prev, multiplier: text }))
                   }
                   keyboardType="decimal-pad"
                   placeholder="e.g. 1.5"
                  editable={!inputsDisabled}
                  style={styles.inlineInput}
                />
                <Text style={styles.inputHint}>x base points</Text>
              </View>
            )}
            {bet.mode === 'fixed' && (
              <View style={styles.inlineInputs}>
                {bet.overrideSides.map((entry) => (
                  <View key={entry.side} style={styles.fixedInputGroup}>
                    <Text style={styles.smallLabel}>{entry.label} Points</Text>
                    <Input
                      value={bet.sideValues[entry.side] ?? ''}
                      onChangeText={(text) =>
                        updateBet(week.weekId, bet.nflBetId, (prev) => ({
                          ...prev,
                          sideValues: { ...prev.sideValues, [entry.side]: text },
                        }))
                      }
                      keyboardType="decimal-pad"
                      editable={!inputsDisabled}
                    />
                  </View>
                ))}
              </View>
            )}
        </View>

        </>
        )}
      </View>
    );
  };

  const renderWeek = (week: WeekFormState, detail: SeasonTemplateDetail['weeks'][number]) => {
    const isExpanded = expandedWeeks.has(week.weekId);
    const weekStartLabel = formatDateLabel(detail.week.startDate);
    const weekEndLabel = formatDateLabel(detail.week.endDate);
    const bets = detail.games.flatMap((game) => game.bets);
    const weekHasLockedBets = week.bets.some((bet) => lockedBetIdSet.has(bet.nflBetId));
    const weekToggleDisabled = disableWeekToggle || weekHasLockedBets;

    const buildOrderedCustomPropIds = (customPropIds: string[]) => {
      const entries = customPropIds.map((id, index) => {
        const betState = betStateById.get(id);
        const order =
          typeof betState?.sortOrder === 'number' && Number.isFinite(betState.sortOrder)
            ? betState.sortOrder
            : index;
        return { id, order, fallback: index };
      });
      return entries
        .sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          if (a.fallback !== b.fallback) return a.fallback - b.fallback;
          return a.id.localeCompare(b.id);
        })
        .map((entry) => entry.id);
    };

    const moveCustomProp = (customPropIds: string[], currentIndex: number, direction: -1 | 1) => {
      if (customPropIds.length < 2) return;
      const orderedIds = buildOrderedCustomPropIds(customPropIds);
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= orderedIds.length) return;
      const nextOrder = [...orderedIds];
      const [moved] = nextOrder.splice(currentIndex, 1);
      if (!moved) return;
      nextOrder.splice(nextIndex, 0, moved);

      const orderMap = new Map(nextOrder.map((id, index) => [id, index]));
      updateWeek(week.weekId, (prev) => ({
        ...prev,
        bets: prev.bets.map((bet) =>
          orderMap.has(bet.nflBetId)
            ? { ...bet, sortOrder: orderMap.get(bet.nflBetId) ?? bet.sortOrder }
            : bet,
        ),
      }));
    };

    return (
      <View key={week.weekId} style={styles.weekCard}>
        <TouchableOpacity style={styles.weekHeader} onPress={() => toggleWeekExpanded(week.weekId)}>
          <View>
            <Text style={styles.weekTitle}>{detail.reference.roundLabel}</Text>
            <Text style={styles.weekSubtitle}>
              {weekStartLabel} → {weekEndLabel} • {bets.length} bets
            </Text>
          </View>
          <View style={styles.weekToggle}>
            <Text style={styles.smallLabel}>Enabled</Text>
            <Switch
              checked={week.enabled}
              onCheckedChange={(value) =>
                updateWeek(week.weekId, (prev) => ({
                  ...prev,
                  enabled: value,
                }))
              }
              disabled={weekToggleDisabled}
            />
          </View>
          {weekHasLockedBets && <Text style={styles.lockedBadge}>Locked Bets Present</Text>}
        </TouchableOpacity>
        {isExpanded && (
          <View style={styles.weekContent}>
            <PropOfTheWeekSelector
              leagueId={leagueId}
              seasonId={seasonId}
              weekId={week.weekId}
              value={week.propOfTheWeekNflBetId ?? null}
              onChange={(value) =>
                updateWeek(week.weekId, (prev) => ({ ...prev, propOfTheWeekNflBetId: value }))
              }
              disabled={submitting || !week.enabled}
            />
            {detail.games.map((game) => {
              const rawGameLabel = (game.bets[0]?.displayInfo as Record<string, unknown> | null)?.gameLabel;
              const gameTitle = typeof rawGameLabel === 'string' ? rawGameLabel : game.game.id;
              const customPropIds = game.bets
                .filter((bet) => bet.nflBetType === 'custom_prop')
                .map((bet) => bet.id);
              const orderedCustomPropIds = buildOrderedCustomPropIds(customPropIds);
              const customPropIndexMap = new Map(orderedCustomPropIds.map((id, index) => [id, index]));
              const customProps = game.bets.filter((bet) => bet.nflBetType === 'custom_prop');
              const orderedCustomProps = [...customProps].sort((a, b) => {
                const aOrder = customPropIndexMap.get(a.id) ?? 0;
                const bOrder = customPropIndexMap.get(b.id) ?? 0;
                if (aOrder !== bOrder) return aOrder - bOrder;
                return a.id.localeCompare(b.id);
              });
              let customPropIndex = 0;
              const orderedBets = game.bets.map((bet) => {
                if (bet.nflBetType !== 'custom_prop') {
                  return bet;
                }
                const replacement = orderedCustomProps[customPropIndex++] ?? bet;
                return replacement;
              });

              return (
                <View key={game.game.id} style={styles.gameContainer}>
                  <Text style={styles.gameTitle}>{gameTitle}</Text>
                  {orderedBets.map((bet) => {
                    const betForm = week.bets.find((b) => b.nflBetId === bet.id);
                    if (!betForm) return null;
                    const rawRoundLabel = (bet.displayInfo as Record<string, unknown> | null)?.roundLabel;
                    const subtitleValue = typeof rawRoundLabel === 'string' ? rawRoundLabel : undefined;
                    const rawSpread = (bet.displayInfo as Record<string, unknown> | null)?.spread;
                    const rawTotal = (bet.displayInfo as Record<string, unknown> | null)?.total;
                    const customLabel =
                      typeof (bet.displayInfo as Record<string, unknown> | null)?.label === 'string'
                        ? ((bet.displayInfo as Record<string, unknown>).label as string).trim()
                        : '';
                    const sideLabelOne =
                      typeof (bet.displayInfo as Record<string, unknown> | null)?.sideLabelOne === 'string'
                        ? ((bet.displayInfo as Record<string, unknown>).sideLabelOne as string).trim()
                        : '';
                    const sideLabelTwo =
                      typeof (bet.displayInfo as Record<string, unknown> | null)?.sideLabelTwo === 'string'
                        ? ((bet.displayInfo as Record<string, unknown>).sideLabelTwo as string).trim()
                        : '';

                    let detailsValue: string | undefined;
                    if (bet.nflBetType === 'spread' && typeof rawSpread === 'number') {
                      detailsValue = `Spread ${rawSpread}`;
                    } else if (bet.nflBetType === 'total' && typeof rawTotal === 'number') {
                      detailsValue = `Total ${rawTotal}`;
                    } else if (bet.nflBetType === 'total_tiebreaker') {
                      detailsValue = 'Closest total points wins.';
                    } else if (bet.nflBetType === 'custom_prop') {
                      const sides = [sideLabelOne, sideLabelTwo].filter(Boolean).join(' vs ');
                      detailsValue = sides || undefined;
                    }
                    const meta: { title: string; subtitle?: string; details?: string } = {
                      title:
                        bet.nflBetType === 'custom_prop'
                          ? customLabel || `CUSTOM PROP • ${gameTitle}`
                          : bet.nflBetType === 'total_tiebreaker'
                            ? `TIEBREAKER • ${gameTitle}`
                            : `${bet.nflBetType.toUpperCase()} • ${gameTitle}`,
                    };
                    if (subtitleValue) meta.subtitle = subtitleValue;
                    if (detailsValue) meta.details = detailsValue;
                    const customPropIndexForBet =
                      bet.nflBetType === 'custom_prop' ? customPropIndexMap.get(bet.id) ?? -1 : -1;
                    const canMoveUp = customPropIndexForBet > 0;
                    const canMoveDown =
                      customPropIndexForBet >= 0 && customPropIndexForBet < orderedCustomPropIds.length - 1;
                    return (
                      <View key={bet.id} style={styles.betWrapper}>
                      <Badge>
                        {bet.nflBetType === 'custom_prop'
                          ? 'CUSTOM'
                          : bet.nflBetType === 'total_tiebreaker'
                            ? 'TIEBREAKER'
                            : bet.nflBetType.toUpperCase()}
                      </Badge>
                        {renderBetControls(
                          week,
                          betForm,
                          meta,
                          bet.nflBetType === 'custom_prop' && orderedCustomPropIds.length > 1
                            ? {
                                canMoveUp,
                                canMoveDown,
                                onMoveUp: () =>
                                  moveCustomProp(customPropIds, customPropIndexForBet, -1),
                                onMoveDown: () =>
                                  moveCustomProp(customPropIds, customPropIndexForBet, 1),
                              }
                            : undefined,
                        )}
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const content = (
    <AppSafeArea style={styles.modalContainer} edges={resolvedSafeAreaEdges}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Configure Season</Text>
          <Text style={styles.modalSubtitle}>Choose a template, then customize the bets you want to include.</Text>
        </View>
        <ScrollView style={styles.modalBody} contentContainerStyle={modalScrollContentStyle}>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Template</Text>
            {summariesLoading ? (
              <ActivityIndicator />
            ) : isTemplateLocked ? (
              <View style={styles.readonlyField}>
                <Text style={styles.readonlyValue}>{selectedTemplateLabel}</Text>
                <Text style={styles.smallLabel}>Template selection is locked for this season.</Text>
              </View>
            ) : (
              <AppSelect
                value={selectedTemplateId || defaultTemplateId}
                onChange={(value) => {
                  setSelectedTemplateId(value);
                  setWeekStates([]);
                }}
                options={(templateSummaries ?? []).map((template) => ({
                  value: template.id,
                  label: template.label,
                }))}
                placeholder="Select a template"
              />
            )}
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Season Label</Text>
            <Input
              value={seasonLabel}
              onChangeText={setSeasonLabel}
              placeholder="e.g. 2025 NFL Playoffs"
            />
          </View>
          <View style={styles.fieldGroup}>
            <View style={styles.betTypeToggleRow}>
              <View style={flexStyle}>
                <Text style={styles.betTypeToggleLabel}>Active Season</Text>
                <Text style={styles.smallLabel}>
                  Make this the default season for all league members.
                </Text>
              </View>
              <View style={styles.betTypeToggleControl}>
                <Text style={styles.smallLabel}>{isActive ? 'Active' : 'Inactive'}</Text>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </View>
            </View>
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Default Scoring System</Text>
            <AppSelect
              value={seasonScoring}
              onChange={(value) => setSeasonScoring(value as SeasonScoringSystem)}
              options={[
                { value: 'oddsBased', label: 'Odds-Based (reward underdogs)' },
                { value: 'totalWins', label: 'Total Wins (1 point per win)' },
              ]}
              placeholder="Select scoring"
            />
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Season/{unitLabelSingular} Mode</Text>
            <AppSelect
              value={seasonWeekMode}
              onChange={(value) => setSeasonWeekMode(value as SeasonWeekMode)}
              options={[
                { value: 'standard', label: 'Standard (All bets count everywhere)' },
                { value: 'propWeeklyOnly', label: 'Weekly Props / Season Picks' },
              ]}
              placeholder="Select mode"
            />
            <Text style={styles.smallLabel}>
              {seasonWeekMode === 'standard' 
                ? 'All bets count toward both weekly and season totals.' 
                : 'Props count for weekly only. Game picks count for season only.'}
            </Text>
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Pick Lock</Text>
            <AppSelect
              value={pickLockMode}
              onChange={(value) => setPickLockMode(value as PickLockMode)}
              options={[
                { value: 'gameStart', label: 'At each game start time' },
                { value: 'roundStart', label: `At first game start time for the ${unitLabelSingular}` },
                { value: 'afterFirstNGames', label: `After first N games in the ${unitLabelSingular}` },
              ]}
              placeholder="Select pick lock"
            />
            {pickLockMode === 'afterFirstNGames' && (
              <View style={styles.inlineInputRow}>
                <Text style={styles.smallLabel}>N games</Text>
                <Input
                  value={pickLockGamesCount}
                  onChangeText={setPickLockGamesCount}
                  placeholder="1"
                  keyboardType="number-pad"
                  style={styles.smallNumberInput}
                />
              </View>
            )}
            <Text style={styles.smallLabel}>
              {pickLockMode === 'gameStart'
                ? 'Each pick locks independently when its game starts.'
                : pickLockMode === 'roundStart'
                  ? `All picks in the ${unitLabelSingular} lock when its first game starts.`
                  : `All picks in the ${unitLabelSingular} lock when the Nth game starts.`}
            </Text>
          </View>
          {supportsSquares && (
            <View style={styles.fieldGroup}>
              <View style={styles.betTypeToggleRow}>
                <View style={flexStyle}>
                  <Text style={styles.betTypeToggleLabel}>Super Bowl squares (experimental)</Text>
                  <Text style={styles.smallLabel}>
                    Enable a 10x10 Super Bowl squares board for the final game of this season.
                  </Text>
                </View>
                <View style={styles.betTypeToggleControl}>
                  <Text style={styles.smallLabel}>{superBowlSquaresEnabled ? 'On' : 'Off'}</Text>
                  <Switch
                    checked={superBowlSquaresEnabled}
                    onCheckedChange={setSuperBowlSquaresEnabled}
                  />
                </View>
              </View>
            </View>
          )}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Weekly Buy-In ($)</Text>
            <Input
              value={weeklyBuyIn}
              onChangeText={setWeeklyBuyIn}
              placeholder="e.g. 10.00"
              keyboardType="decimal-pad"
            />
            <Text style={styles.smallLabel}>Set to 0 for free weekly play.</Text>
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Season Buy-In ($)</Text>
            <Input
              value={seasonBuyIn}
              onChangeText={setSeasonBuyIn}
              placeholder="Optional"
              keyboardType="decimal-pad"
            />
            <Text style={styles.smallLabel}>Paid to the top scorer at the end of the season</Text>
          </View>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Bet Type Toggles</Text>
            {(
              [
                { type: 'moneyline', label: 'Moneyline bets' },
                { type: 'spread', label: 'Spread bets' },
                { type: 'total', label: 'Total bets' },
                { type: 'total_tiebreaker', label: 'Total tiebreakers' },
                { type: 'custom_prop', label: 'Custom prop bets' },
              ] as { type: SeasonTemplateBetType; label: string }[]
            ).map(({ type, label }) => {
              const stats = betTypeStats[type];
              const hasBets = stats.totalCount > 0;
              const hasMutableBets = stats.mutableCount > 0;
              const checked =
                type === 'total_tiebreaker'
                  ? hasMutableBets
                    ? stats.enabledMutableCount > 0
                    : stats.totalCount > 0 && stats.enabledCount === stats.totalCount
                  : hasMutableBets
                    ? stats.enabledMutableCount === stats.mutableCount
                    : stats.totalCount > 0 && stats.enabledCount === stats.totalCount;
              return (
                <View key={type} style={styles.betTypeToggleRow}>
                  <View style={flexStyle}>
                    <Text style={styles.betTypeToggleLabel}>{label}</Text>
                    {!hasBets && <Text style={styles.smallLabel}>No bets of this type.</Text>}
                    {hasBets && !hasMutableBets && (
                      <Text style={styles.smallLabel}>All bets of this type are locked.</Text>
                    )}
                    {type === 'total_tiebreaker' && hasBets && hasMutableBets && (
                      <Text style={styles.smallLabel}>On enables the last game of each week only.</Text>
                    )}
                  </View>
                  <View style={styles.betTypeToggleControl}>
                    <Text style={styles.smallLabel}>Enabled</Text>
                    <Switch
                      checked={checked}
                      onCheckedChange={(value) => updateBetTypeToggle(type, value)}
                      disabled={!hasBets || !hasMutableBets}
                    />
                  </View>
                </View>
              );
            })}
          </View>
          {detailLoading && <ActivityIndicator style={detailLoadingStyle} />}
          {templateDetail && weekStates.length > 0 && (
            <View style={styles.weeksContainer}>
              {weekStates.map((week) => {
                const detail = templateDetail.weeks.find((w) => w.week.id === week.weekId);
                if (!detail) return null;
                return renderWeek(week, detail);
              })}
            </View>
          )}
        </ScrollView>
        <View style={styles.footer}>
          <Button variant="outline" onPress={onClose} style={styles.footerButton} disabled={submitting}>
            Cancel
          </Button>
          <Button onPress={handleSubmit} style={styles.footerButton} disabled={submitting || !selectedTemplateId}>
            {submitting ? 'Saving...' : submitLabel}
          </Button>
        </View>
      </AppSafeArea>
  );

  if (presentation === 'screen') {
    return content;
  }

  if (Platform.OS === 'web') {
    return visible ? (
      <View style={[StyleSheet.absoluteFill, styles.webBackdrop]}>
        {content}
      </View>
    ) : null;
  }

  return (
    <Modal visible={visible} onRequestClose={onClose} animationType="slide">
      <SafeAreaProvider>{content}</SafeAreaProvider>
    </Modal>
  );
}

const styles = StyleSheet.create(theme => ({
  betHeader: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  betMeta: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  betRow: {
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    marginTop: 6,
    padding: 12,
  },
  betSubtitle: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 12,
  },
  betTitle: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
  },
  betToggle: {
    alignItems: 'center',
  },
  betTypeToggleControl: {
    alignItems: 'center',
  },
  betTypeToggleLabel: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
  },
  betTypeToggleRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  betWrapper: {
    marginBottom: 12,
  },
  clearPropText: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fixedInputGroup: {
    flex: 1,
    minWidth: 140,
  },
  footer: {
    backgroundColor: theme.colors.background,
    borderTopColor: theme.colors.press,
    borderTopWidth: 5,
    bottom: 0,
    flexDirection: 'row',
    gap: 12,
    left: 0,
    padding: 16,
    position: 'absolute',
    right: 0,
  },
  footerButton: {
    flex: 1,
  },
  gameContainer: {
    borderTopColor: theme.colors.press,
    borderTopWidth: 3,
    marginTop: 12,
    paddingTop: 12,
  },
  gameTitle: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  inlineInput: {
    flex: 1,
  },
  inlineInputRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  inlineInputs: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  inputHint: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 12,
  },
  label: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.7,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  lockedBadge: {
    backgroundColor: theme.colors.errorBackground,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    color: theme.colors.destructive,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  lockedNotice: {
    color: theme.colors.warning,
    marginBottom: 8,
  },
  modalBody: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  modalContainer: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  modalHeader: {
    backgroundColor: theme.colors.background,
    borderBottomColor: theme.colors.press,
    borderBottomWidth: 5,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  modalSubtitle: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 14,
    marginTop: 4,
  },
  modalTitle: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.display,
    fontSize: 26,
    fontWeight: '900',
    letterSpacing: -0.5,
    textTransform: 'uppercase',
  },
  modeChip: {
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  modeChipActive: {
    backgroundColor: theme.colors.acid,
    borderColor: theme.colors.press,
  },
  modeChipText: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  modeChipTextActive: {
    color: theme.colors.press,
  },
  modeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  overrideContainer: {
    gap: 8,
    marginTop: 12,
  },
  propOfTheWeekBox: {
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderWidth: 2,
    gap: 8,
    marginBottom: 16,
    padding: 12,
  },
  propOfTheWeekHint: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 12,
  },
  propSearchResult: {
    borderBottomColor: theme.colors.pressMuted,
    borderBottomWidth: 1,
    padding: 10,
  },
  propSearchResultLabel: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
  },
  propSearchResults: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.press,
    borderWidth: 2,
    gap: 0,
  },
  readonlyField: {
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    padding: 12,
  },
  readonlyValue: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  reorderButton: {
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  reorderButtonDisabled: {
    opacity: 0.5,
  },
  reorderButtonText: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  reorderControls: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  selectedPropLabel: {
    color: theme.colors.press,
    flex: 1,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
  },
  selectedPropRow: {
    alignItems: 'center',
    borderColor: theme.colors.press,
    borderWidth: 2,
    flexDirection: 'row',
    gap: 8,
    padding: 8,
  },
  smallLabel: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 12,
    marginBottom: 4,
  },
  smallNumberInput: {
    width: 96,
  },
  webBackdrop: {
    backgroundColor: theme.colors.background,
  },
  weekCard: {
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    shadowColor: theme.colors.press,
    shadowOffset: { width: 6, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  weekContent: {
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  weekHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  weekSubtitle: {
    color: theme.colors.pressMuted,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 12,
    marginTop: 2,
  },
  weekTitle: {
    color: theme.colors.press,
    fontFamily: theme.typography.fontFamily.bodyMedium,
    fontSize: 16,
    fontWeight: '600',
  },
  weekToggle: {
    alignItems: 'center',
  },
  weeksContainer: {
    gap: 12,
    marginTop: 8,
  },
}));
